import React, { useState } from "react";
import ReactDOM from "react-dom";

const CT_STATUS_BADGE = {
    "COMPLETED":                  "discovery-badge-green",
    "RECRUITING":                 "discovery-badge-blue",
    "NOT_YET_RECRUITING":         "discovery-badge-yellow",
    "ACTIVE_NOT_RECRUITING":      "discovery-badge-orange",
    "TERMINATED":                 "discovery-badge-red",
    "WITHDRAWN":                  "discovery-badge-red",
    "SUSPENDED":                  "discovery-badge-orange",
    "ENROLLING_BY_INVITATION":    "discovery-badge-blue",
    "UNKNOWN":                    "discovery-badge-neutral",
};

const DETAIL_TABS = [
    { key: "study_details",   label: "Study Details" },
    { key: "researcher_view", label: "Researcher View" },
    { key: "no_results",      label: "No Results Posted" },
    { key: "record_history",  label: "Record History" },
];

// ── Reusable collapsible section ──
const Section = ({ title, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="discovery-section mb-2">
            <button type="button" className="discovery-section-header" onClick={() => setOpen(o => !o)}>
                <span className="ev-small-p-dark ev-bold-med text-truncate">{title}</span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11 flex-shrink-0`} />
            </button>
            {open && <div className="discovery-section-body">{children}</div>}
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="d-flex gap-2 mb-1">
        <span className="ev-xsmall-p-dark ev-bold-med text-nowrap" style={{ minWidth: "140px" }}>{label}:</span>
        <span className="ev-xsmall-p-dark">{value || "—"}</span>
    </div>
);

const ClinicalTrialsDetailPanel = ({ trial }) => {
    const [activeDetailTab, setActiveDetailTab] = useState("study_details");
    const [previewUrl, setPreviewUrl]           = useState(null);
    const [previewName, setPreviewName]         = useState("");

    if (!trial) {
        return (
            <div className="ev-card-2 p-4 vh-55 d-flex flex-column align-items-center justify-content-center">
                <i className="bi bi-clipboard2-pulse ev-fs-50 text-muted mb-3"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">No Trial Selected</p>
                <p className="ev-small-p-dark text-muted text-center mb-0">
                    Select a trial from the list to view its details.
                </p>
            </div>
        );
    }

    // ── Map backend fields ──
    const nctId      = trial.nct_id || "—";
    const status     = trial.overall_status || "—";
    const phase      = trial.phase || "—";
    const title      = trial.brief_title || trial.official_title || "—";
    const summary    = trial.brief_summary || "—";
    const detailed   = trial.detailed_description || null;
    const studyType  = trial.study_type || "—";
    const enrollment = trial.enrollment_count || "—";
    const startDate  = trial.start_date || "—";
    const completionDate = trial.completion_date || "—";
    const sponsor    = trial.lead_sponsor || "—";
    const hasResults = trial.has_results || false;
    const ctUrl      = nctId !== "—" ? `https://clinicaltrials.gov/ct2/show/${nctId}` : null;

    // ── Conditions — array of objects ──
    const conditions = Array.isArray(trial.conditions)
        ? trial.conditions.map(c => c.condition_name).filter(Boolean)
        : [];

    // ── Eligibility — from first condition object ──
    const eligibility = Array.isArray(trial.conditions) && trial.conditions.length > 0
        ? trial.conditions[0].eligibility_criteria || null
        : null;
    const sex    = Array.isArray(trial.conditions) && trial.conditions[0]?.sex || null;
    const minAge = Array.isArray(trial.conditions) && trial.conditions[0]?.min_age || null;
    const maxAge = Array.isArray(trial.conditions) && trial.conditions[0]?.max_age || null;

    // ── Interventions — array of objects ──
    const interventions = Array.isArray(trial.interventions) ? trial.interventions : [];

    // ── Locations — array of objects ──
    const locations = Array.isArray(trial.locations) ? trial.locations : [];

    // ── Documents ──
    const documents = Array.isArray(trial.documents) ? trial.documents : [];

    // ── Results ──
    const trialResults = trial.results || null;

    return (
        <div className="ev-card-2 p-3 pe-1 vh-55 d-flex flex-column position-relative">

            {/* ── Fixed header ── */}
            <div className="flex-shrink-0 pe-2 mb-2">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <span className="ev-badge-pill ev-fs-11 ev-badge-blue flex-shrink-0">{nctId}</span>
                    {phase !== "—" && (
                        <span className="ev-badge-pill ev-fs-11 ev-badge-light flex-shrink-0">{phase}</span>
                    )}
                    <span className={`flex-shrink-0 ${CT_STATUS_BADGE[status] || "discovery-badge-neutral"}`}>
                        {status.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                    </span>
                </div>
                <p className="ev-bold-med ev-reg-p-dark mb-1">{title}</p>
                <p className="ev-xsmall-p-dark mb-2">Sponsor: {sponsor}</p>

                {/* Tab pills — Canvas TabViewer style */}
                <div className="ev-message-card5 m-0 ev-padding-xsmall2 ps-0 d-flex align-items-center flex-wrap gap-1">
                    {DETAIL_TABS.map((tab) => (
                        <span
                            key={tab.key}
                            className={`rounded-pill ev-btn-xsmall ms-1 text-nowrap cursor-pointer ${
                                activeDetailTab === tab.key ? "ev-btn-blue-fill-round" : "ev-message-card2"
                            }`}
                            onClick={() => setActiveDetailTab(tab.key)}
                        >
                            <b>{tab.label}</b>
                        </span>
                    ))}
                </div>
            </div>

            {/* ── Scrollable content ── */}
            <div className="flex-grow-1 scrollable-element pe-2">

                {/* ══ TAB 1: Study Details ══ */}
                {activeDetailTab === "study_details" && (
                    <>
                        <Section title="Study Overview" defaultOpen={true}>
                            <InfoRow label="NCT Number"   value={nctId} />
                            <InfoRow label="Status" value={status.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase())} />
                            <InfoRow label="Phase"        value={phase} />
                            <InfoRow label="Study Type"   value={studyType} />
                            <InfoRow label="Enrollment"   value={enrollment ? `${enrollment} participants` : "—"} />
                            <InfoRow label="Start Date"   value={startDate} />
                            <InfoRow label="Completion"   value={completionDate} />
                            {ctUrl && (
                                <div className="d-flex gap-2 mb-1">
                                    <span className="ev-xsmall-p-dark ev-bold-med text-nowrap" style={{ minWidth: "140px" }}>ClinicalTrials.gov:</span>
                                    <a href={ctUrl} target="_blank" rel="noopener noreferrer" className="ev-xsmall-p-dark">
                                        {nctId} <i className="bi bi-box-arrow-up-right ev-fs-10 ms-1"></i>
                                    </a>
                                </div>
                            )}
                        </Section>

                        <Section title="Brief Summary">
                            <p className="ev-xsmall-p-dark mb-0" style={{ whiteSpace: "pre-wrap" }}>{summary}</p>
                        </Section>

                        {detailed && (
                            <Section title="Detailed Description">
                                <p className="ev-xsmall-p-dark mb-0" style={{ whiteSpace: "pre-wrap" }}>{detailed}</p>
                            </Section>
                        )}

                        <Section title="Conditions & Interventions">
                            {conditions.length > 0 && (
                                <div className="mb-2">
                                    <p className="ev-xsmall-p-dark ev-bold-med mb-1">Conditions</p>
                                    <div className="d-flex flex-wrap gap-1">
                                        {conditions.map((c, i) => (
                                            <span key={i} className="ev-badge-pill ev-fs-11 ev-badge-light">{c}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {interventions.length > 0 && (
                                <div>
                                    <p className="ev-xsmall-p-dark ev-bold-med mb-1">Interventions</p>
                                    <div className="table-container">
                                        <table className="table-custom1 w-100 mt-0">
                                            <thead>
                                                <tr>
                                                    <th><label>Type</label></th>
                                                    <th><label>Name</label></th>
                                                    <th><label>Description</label></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {interventions.map((inv, i) => (
                                                    <tr key={i}>
                                                        <td className="ev-small-p-dark">{inv.intervention_type || "—"}</td>
                                                        <td className="ev-small-p-dark ev-bold-med">{inv.intervention_name || "—"}</td>
                                                        <td className="ev-small-p-dark">{inv.description || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {conditions.length === 0 && interventions.length === 0 && (
                                <p className="ev-xsmall-p-dark text-muted mb-0">No conditions or interventions available.</p>
                            )}
                        </Section>

                        <Section title="Eligibility Criteria">
                            {(sex || minAge || maxAge) && (
                                <div className="mb-2">
                                    {sex    && <InfoRow label="Sex"     value={sex} />}
                                    {minAge && <InfoRow label="Min Age" value={minAge} />}
                                    {maxAge && <InfoRow label="Max Age" value={maxAge} />}
                                </div>
                            )}
                            {eligibility
                                ? <p className="ev-xsmall-p-dark mb-0" style={{ whiteSpace: "pre-wrap" }}>{eligibility}</p>
                                : <p className="ev-xsmall-p-dark text-muted mb-0">No eligibility criteria available.</p>
                            }
                        </Section>

                        <Section title="Contacts & Locations">
                            {locations.length > 0 ? (
                                <div className="table-container">
                                    <table className="table-custom1 w-100 mt-0">
                                        <thead>
                                            <tr>
                                                <th><label>Facility</label></th>
                                                <th><label>City</label></th>
                                                <th><label>Country</label></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {locations.map((loc, i) => (
                                                <tr key={i}>
                                                    <td className="ev-small-p-dark">{loc.facility || "—"}</td>
                                                    <td className="ev-small-p-dark">{[loc.city, loc.state].filter(Boolean).join(", ") || "—"}</td>
                                                    <td className="ev-small-p-dark">{loc.country || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="ev-xsmall-p-dark text-muted mb-0">No location information available.</p>
                            )}
                        </Section>
                    </>
                )}

                {/* ══ TAB 2: Researcher View ══ */}
                {activeDetailTab === "researcher_view" && (
                    <>
                        <Section title="Study Identification" defaultOpen={true}>
                            <InfoRow label="NCT Number"  value={nctId} />
                            <InfoRow label="Study Type"  value={studyType} />
                            <InfoRow label="Phase"       value={phase} />
                            <InfoRow label="Sponsor"     value={sponsor} />
                            <InfoRow label="Enrollment"  value={enrollment ? `${enrollment} participants` : "—"} />
                            <InfoRow label="Start Date"  value={startDate} />
                            <InfoRow label="Completion"  value={completionDate} />
                        </Section>

                        <Section title="Arms & Interventions">
                            {interventions.length > 0 ? (
                                <div className="table-container">
                                    <table className="table-custom1 w-100 mt-0">
                                        <thead>
                                            <tr>
                                                <th><label>Arm/Group</label></th>
                                                <th><label>Type</label></th>
                                                <th><label>Name</label></th>
                                                <th><label>Description</label></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {interventions.map((inv, i) => (
                                                <tr key={i}>
                                                    <td className="ev-small-p-dark">{inv.arm_group_label || `Arm ${i + 1}`}</td>
                                                    <td className="ev-small-p-dark">{inv.intervention_type || "—"}</td>
                                                    <td className="ev-small-p-dark ev-bold-med">{inv.intervention_name || "—"}</td>
                                                    <td className="ev-small-p-dark">{inv.description || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="ev-xsmall-p-dark text-muted mb-0">No arms or interventions available.</p>
                            )}
                        </Section>

                        <Section title="Eligibility">
                            {(sex || minAge || maxAge) && (
                                <div className="mb-2">
                                    {sex    && <InfoRow label="Sex"     value={sex} />}
                                    {minAge && <InfoRow label="Min Age" value={minAge} />}
                                    {maxAge && <InfoRow label="Max Age" value={maxAge} />}
                                </div>
                            )}
                            {eligibility
                                ? <p className="ev-xsmall-p-dark mb-0" style={{ whiteSpace: "pre-wrap" }}>{eligibility}</p>
                                : <p className="ev-xsmall-p-dark text-muted mb-0">No eligibility criteria available.</p>
                            }
                        </Section>

                        {documents.length > 0 && (
                            <Section title="Study Documents">
                                <div className="table-container">
                                    <table className="table-custom1 w-100 mt-0">
                                        <thead>
                                            <tr>
                                                <th><label>Type</label></th>
                                                <th><label>Label</label></th>
                                                <th><label>Link</label></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {documents.map((doc, i) => (
                                                <tr key={i}>
                                                    <td className="ev-small-p-dark">{doc.doc_type || "—"}</td>
                                                    <td className="ev-small-p-dark">{doc.label || "—"}</td>
                                                    <td className="ev-small-p-dark">
                                                        {doc.document_url ? (
                                                            <button
                                                                type="button"
                                                                className="ev-btn-lightblue-fill-round ev-btn-xsmall"
                                                                onClick={() => { setPreviewUrl(doc.document_url); setPreviewName(doc.label || "Document"); }}
                                                            >
                                                                <i className="bi bi-eye me-1"></i>View
                                                            </button>
                                                        ) : "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Section>
                        )}
                    </>
                )}

                {/* ══ TAB 3: No Results Posted ══ */}
                {activeDetailTab === "no_results" && (
                    hasResults && trialResults ? (
                        <>
                            <Section title="Results Summary" defaultOpen={true}>
                                <InfoRow label="Participants"       value={trialResults.participant_count} />
                                <InfoRow label="Results Posted"     value={trialResults.results_first_posted} />
                            </Section>
                        </>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                            <i className="bi bi-clipboard-x ev-fs-50 text-muted mb-3"></i>
                            <p className="ev-reg-p-dark ev-bold-med mb-1">No Results Posted</p>
                            <p className="ev-small-p-dark text-muted text-center mb-0">
                                Results for <b>{nctId}</b> have not been posted yet.
                            </p>
                            {ctUrl && (
                                <a
                                    href={ctUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ev-btn-blue-outline-round ev-btn-small mt-3 d-flex align-items-center gap-1"
                                >
                                    <i className="bi bi-box-arrow-up-right"></i> View on ClinicalTrials.gov
                                </a>
                            )}
                        </div>
                    )
                )}

                {/* ══ TAB 4: Record History ══ */}
                {activeDetailTab === "record_history" && (
                    <>
                        <Section title="Study Record Dates" defaultOpen={true}>
                            <div className="table-container">
                                <table className="table-custom1 w-100 mt-0">
                                    <thead>
                                        <tr>
                                            <th><label>Event</label></th>
                                            <th><label>Date</label></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="ev-small-p-dark ev-bold-med">Study Start</td>
                                            <td className="ev-small-p-dark">{startDate}</td>
                                        </tr>
                                        <tr>
                                            <td className="ev-small-p-dark ev-bold-med">Primary Completion</td>
                                            <td className="ev-small-p-dark">{completionDate}</td>
                                        </tr>
                                        {trialResults?.results_first_posted && (
                                            <tr>
                                                <td className="ev-small-p-dark ev-bold-med">Results First Posted</td>
                                                <td className="ev-small-p-dark">{trialResults.results_first_posted}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Section>

                        <Section title="More Information">
                            <InfoRow label="NCT Number" value={nctId} />
                            <InfoRow label="Sponsor"    value={sponsor} />
                            <InfoRow label="Phase"      value={phase} />
                            <InfoRow label="Status" value={status.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase())} />
                            <InfoRow label="Has Results" value={hasResults ? "Yes" : "No"} />
                            {ctUrl && (
                                <div className="d-flex gap-2 mb-1 align-items-center">
                                    <span className="ev-xsmall-p-dark ev-bold-med text-nowrap" style={{ minWidth: "140px" }}>ClinicalTrials.gov:</span>
                                    <a href={ctUrl} target="_blank" rel="noopener noreferrer" className="ev-xsmall-p-dark">
                                        {nctId} <i className="bi bi-box-arrow-up-right ev-fs-10 ms-1"></i>
                                    </a>
                                </div>
                            )}
                        </Section>
                    </>
                )}

            </div>

            {/* ── PDF Preview Modal ── */}
            {!!previewUrl && ReactDOM.createPortal(
                <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }} onClick={e => e.stopPropagation()}>
                    <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                        <div className="modal-content ev-card-3">
                            <div className="row modal-header border-0">
                                <div className="col-10">
                                    <h6 className="ev-message-card3 m-0">{previewName || "Document Preview"}</h6>
                                </div>
                                <div className="col-2 text-end d-flex align-items-center justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn ev-btn-lightblue-fill-round ev-radius-xsmall ev-btn-med avatar-tooltip"
                                        data-tooltip="Open in New Tab"
                                        onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                                    >
                                        <i className="bi bi-box-arrow-up-right"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="ev-btn-text-only-fill ev-fs-20"
                                        onClick={() => { setPreviewUrl(null); setPreviewName(""); }}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body p-0 ev-radius-small vh-80">
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                                    title={previewName || "Document Preview"}
                                    width="100%"
                                    height="100%"
                                    style={{ border: "none", minHeight: "75vh" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ClinicalTrialsDetailPanel;